import { print } from 'graphql';
import { gql } from 'graphql-tag';
import { AD_TARGETING_FRAGMENT } from './adTargeting.fragment';
import { ANALYTICS_ECCOMERCE_FRAGMENT } from './analyticsEcommerce.fragment';
import { ANALYTICS_TAXONOMY_FRAGMENT } from './analyticTaxonomy.fragment';
import { LINKS_FRAGMENT } from './links.fragment';
import { LISTING_FRAGMENT } from './listing.fragment';
import { LISTING_SS_FRAGMENT } from './listingSS.fragment';
import { META_INFO_FRAGMENT } from './metaInfo.fragment';
import { PAGINATION_FRAGMENT } from './pagination.fragment';
import { SAVED_SEARCH_AND_ALERTS_FRAGMENT } from './savedSearchAndAlerts.fragment';
import { SEO_FRAGMENT } from './seo.fragment';
import { SEO_ACCORDIONS_FRAGMENT } from './seoAccordions.fragment';
import { SEO_BLURB_FRAGMENT } from './seoBlurb.fragment';

export const LOG_SEARCH_MUTATION_RAW = gql`
    mutation logSearch($path: String!) {
        logSearch(url: $path)
    }
`;

export const LOG_SEARCH_MUTATION = print(LOG_SEARCH_MUTATION_RAW);

export const SEARCH_BY_URL_QUERY = print(gql`
    ${ANALYTICS_TAXONOMY_FRAGMENT}
    ${AD_TARGETING_FRAGMENT}
    ${ANALYTICS_ECCOMERCE_FRAGMENT}
    ${META_INFO_FRAGMENT}
    ${PAGINATION_FRAGMENT}
    ${LISTING_SS_FRAGMENT}
    ${LINKS_FRAGMENT}
    ${SEO_BLURB_FRAGMENT}
    ${SAVED_SEARCH_AND_ALERTS_FRAGMENT}
    ${SEO_FRAGMENT}

    query apiSearchByUrl($path: String!) {
        seo(path: $path) {
            ... on SeoResults {
                ...SEO_FRAGMENT
            }
            ... on SeoResultsError {
                errorCode
            }
        }
        searchResults: searchByUrl(url: $path) {
            adTargeting {
                ...AD_TARGETING_FRAGMENT
            }
            analyticsEcommerce {
                ...ANALYTICS_ECCOMERCE_FRAGMENT
            }
            analyticsTaxonomy {
                ...ANALYTICS_TAXONOMY_FRAGMENT
            }
            links {
                ...LINKS_FRAGMENT
            }
            listings {
                regular {
                    ...LISTING_SS_FRAGMENT
                }
                extended {
                    ...LISTING_SS_FRAGMENT
                }
                featured {
                    ...LISTING_SS_FRAGMENT
                }
            }
            metaInfo {
                ...META_INFO_FRAGMENT
            }
            pagination {
                ...PAGINATION_FRAGMENT
            }
            savedSearchAndAlerts {
                ...SAVED_SEARCH_AND_ALERTS_FRAGMENT
            }
            seoBlurb {
                ...SEO_BLURB_FRAGMENT
            }
            title
            userAlertId
        }
    }
`);

export const LISTINGS_QUERY = gql`
    ${ANALYTICS_TAXONOMY_FRAGMENT}
    ${AD_TARGETING_FRAGMENT}
    ${ANALYTICS_ECCOMERCE_FRAGMENT}
    ${META_INFO_FRAGMENT}
    ${PAGINATION_FRAGMENT}
    ${LISTING_FRAGMENT}
    ${LINKS_FRAGMENT}
    ${SEO_BLURB_FRAGMENT}
    ${SAVED_SEARCH_AND_ALERTS_FRAGMENT}
    ${SEO_ACCORDIONS_FRAGMENT}

    query getListingData($path: String!) {
        breadcrumbs(path: $path) {
            label
            tracking
            uri
            noAppend
            i18nLabelKey
        }
        redirects(path: $path) {
            code
            redirectUri
        }
        seoAccordions(path: $path) {
            ...SEO_ACCORDIONS_FRAGMENT
        }
        searchResults(path: $path) {
            analyticsTaxonomy {
                ...ANALYTICS_TAXONOMY_FRAGMENT
            }
            analyticsEcommerce {
                ...ANALYTICS_ECCOMERCE_FRAGMENT
            }
            adTargeting {
                ...AD_TARGETING_FRAGMENT
            }
            metaInfo {
                ...META_INFO_FRAGMENT
            }
            pagination {
                ...PAGINATION_FRAGMENT
            }
            listings {
                regular {
                    ...LISTING_FRAGMENT
                }
                extended {
                    ...LISTING_FRAGMENT
                }
                featured {
                    ...LISTING_FRAGMENT
                }
            }
            links {
                ...LINKS_FRAGMENT
            }
            seoBlurb {
                ...SEO_BLURB_FRAGMENT
            }
            title
            userAlertId
            savedSearchAndAlerts {
                ...SAVED_SEARCH_AND_ALERTS_FRAGMENT
            }
            polyenc
        }
    }
`;

export const LISTINGS_QUERY_STR = print(LISTINGS_QUERY);

export const USER_FAVOURITES_QUERY = gql`
    query userFavourites {
        favourites {
            listings
        }
    }
`;

export const SAVE_LISTING = gql`
    mutation favouriteListing($id: ID!) {
        saveFavouriteListing(id: $id)
    }
`;

export const DELETE_LISTING = gql`
    mutation unfavouriteListing($id: ID!) {
        deleteFavouriteListing(id: $id)
    }
`;

export const CONTACT_AGENTS = gql`
    mutation emailAll($input: EmailAllDetails!) {
        contactAgents(input: $input) {
            emailAll {
                action
                branches {
                    checked
                    featured
                    label
                    logo
                    name
                    type
                    telephone
                    value
                }
                branchesValidation {
                    requiredError
                }
                formConfig {
                    fields {
                        fieldset {
                            fields {
                                name
                                placeholder
                                type
                                value
                                i18NLabelKey
                                maxlength
                                optional
                                validationData {
                                    pattern
                                    patternError
                                    required
                                    requiredError
                                }
                                options {
                                    disabled
                                    i18NLabelKey
                                    label
                                    selected
                                    value
                                }
                            }
                            hideLegend
                            i18NLegendKey
                            name
                        }
                    }
                }
                showCaptcha
            }
            result {
                branches {
                    address
                    branchDetailsUri
                    branchDetailsUrl
                    branchId
                    companyId
                    companyName
                    logoUrl
                    memberType
                    name
                    phone
                    postcode
                }
                leadIdentifier
                message
                trackingObjects {
                    ecommerce {
                        purchase {
                            actionField {
                                affiliation
                                id
                                revenue
                            }
                            products {
                                brand
                                category
                                coupon
                                id
                                name
                                position
                                price
                                quantity
                                variant
                            }
                        }
                    }
                }
            }
        }
    }
`;

export const EMAIL_ALL_QUERY = gql`
    query getEmailAll($path: String!) {
        emailAll(path: $path) {
            action
            branches {
                checked
                featured
                label
                logo
                name
                type
                telephone
                value
            }
            formConfig {
                fields {
                    fieldset {
                        fields {
                            name
                            placeholder
                            type
                            value
                            i18NLabelKey
                            maxlength
                            optional
                            validationData {
                                pattern
                                required
                            }
                            options {
                                disabled
                                i18NLabelKey
                                label
                                selected
                                value
                            }
                        }
                        hideLegend
                        i18NLegendKey
                        name
                    }
                }
            }
            showCaptcha
        }
    }
`;

export const LISTINGS_QUERY_V2 = gql`
    ${ANALYTICS_TAXONOMY_FRAGMENT}
    ${AD_TARGETING_FRAGMENT}
    ${ANALYTICS_ECCOMERCE_FRAGMENT}
    ${META_INFO_FRAGMENT}
    ${PAGINATION_FRAGMENT}
    ${LISTING_FRAGMENT}
    ${LINKS_FRAGMENT}
    ${SEO_BLURB_FRAGMENT}
    ${SAVED_SEARCH_AND_ALERTS_FRAGMENT}

    query getListingDataV2($path: String!) {
        breadcrumbs(path: $path) {
            label
            tracking
            uri
            noAppend
            i18nLabelKey
        }
        redirects(path: $path) {
            code
            redirectUri
        }
        seoAccordions(path: $path) {
            category
            expanded
            geo
            name
            propertyType
            section
            transactionType
            rows {
                links {
                    category
                    geo
                    propertyType
                    section
                    transactionType
                    uri
                }
            }
            links {
                category
                geo
                propertyType
                section
                transactionType
                uri
            }
        }
        searchResultsV2(input: { path: $path }) {
            analyticsTaxonomy {
                ...ANALYTICS_TAXONOMY_FRAGMENT
            }
            analyticsEcommerce {
                ...ANALYTICS_ECCOMERCE_FRAGMENT
            }
            adTargeting {
                ...AD_TARGETING_FRAGMENT
            }
            metaInfo {
                ...META_INFO_FRAGMENT
            }
            pagination {
                ...PAGINATION_FRAGMENT
            }
            results {
                main {
                    data {
                        ... on FeedListingItem {
                            listing {
                                ...LISTING_FRAGMENT
                            }
                            presentationType
                        }
                    }
                }
                extended {
                    ...LISTING_FRAGMENT
                }
            }
            links {
                ...LINKS_FRAGMENT
            }
            seoBlurb {
                ...SEO_BLURB_FRAGMENT
            }
            title
            userAlertId
            savedSearchAndAlerts {
                ...SAVED_SEARCH_AND_ALERTS_FRAGMENT
            }
            polyenc
        }
    }
`;

export const LISTINGS_QUERY_V2_STR = print(LISTINGS_QUERY_V2);
